import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { signOut } from 'firebase/auth';
import TicketList from './TicketList';
import TodoView from './TodoView'; // Import der TodoView-Komponente

function Dashboard() {
  const [tickets, setTickets] = useState([]);
  const [view, setView] = useState('list'); // 'list', 'kanban', 'calendar', 'analytics', 'todo'
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    inProgress: 0,
    resolved: 0,
    highPriority: 0
  });
  const [loading, setLoading] = useState(true);

  // Aktuelle Uhrzeit für die Dashboard-Anzeige
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Update Zeit jede Minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Tickets aus Firestore abfragen und nach Erstellungsdatum sortieren
    const q = query(collection(db, 'tickets'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ticketData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTickets(ticketData);
      
      // Statistiken berechnen
      setStats({
        total: ticketData.length,
        new: ticketData.filter(t => t.status === 'new').length,
        inProgress: ticketData.filter(t => t.status === 'in-progress').length,
        resolved: ticketData.filter(t => t.status === 'resolved').length,
        highPriority: ticketData.filter(t => t.priority === 'hoch').length
      });
      
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = '/login';
  };

  // Helferfunktion für die Anzeige des Datums
  const formatDate = (date) => {
    return date.toLocaleDateString('de-DE', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Helferfunktion für Uhrzeit
  const formatTime = (date) => {
    return date.toLocaleTimeString('de-DE', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div style={{ 
      fontFamily: 'Inter, system-ui, sans-serif',
      backgroundColor: '#f5f7fd',
      minHeight: '100vh',
      display: 'flex'
    }}>
      {/* Sidebar */}
      <div style={{ 
        width: sidebarCollapsed ? '80px' : '250px',
        background: 'white',
        borderRight: '1px solid #eaedf3',
        transition: 'width 0.3s ease',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 1000,
        boxShadow: '2px 0 10px rgba(0,0,0,0.05)',
        overflow: 'hidden'
      }}>
        {/* Sidebar Header with Logo */}
        <div style={{ 
          padding: '20px', 
          borderBottom: '1px solid #eaedf3',
          display: 'flex',
          alignItems: 'center'
        }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            borderRadius: '8px', 
            backgroundColor: '#f0eafb',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginRight: '12px',
            flexShrink: 0
          }}>
            <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#5b21b6' }}>F</span>
          </div>
          {!sidebarCollapsed && (
            <div>
              <h1 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>Farukiyye</h1>
              <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>Feedback Dashboard</p>
            </div>
          )}
        </div>

        {/* Sidebar Menu Items */}
        <div style={{ padding: '15px' }}>
          <p style={{ 
            fontSize: '12px', 
            color: '#94a3b8', 
            fontWeight: 'bold',
            letterSpacing: '1px',
            margin: '0 0 10px 10px',
            display: sidebarCollapsed ? 'none' : 'block'
          }}>
            MENU
          </p>
          
          <MenuItem 
            icon="🏠" 
            title="Dashboard" 
            active={view === 'list'} 
            collapsed={sidebarCollapsed} 
            onClick={() => setView('list')}
          />
          <MenuItem 
            icon="📋" 
            title="Tickets" 
            active={view === 'tickets'} 
            collapsed={sidebarCollapsed} 
            onClick={() => setView('tickets')}
          />
          <MenuItem 
            icon="✅" 
            title="Aufgaben" 
            active={view === 'todo'} 
            collapsed={sidebarCollapsed} 
            onClick={() => setView('todo')}
          />
          <MenuItem 
            icon="📅" 
            title="Kalender" 
            active={view === 'calendar'} 
            collapsed={sidebarCollapsed} 
            onClick={() => setView('calendar')}
          />
          <MenuItem 
            icon="📊" 
            title="Statistiken" 
            active={view === 'analytics'} 
            collapsed={sidebarCollapsed} 
            onClick={() => setView('analytics')}
          />
          
          <p style={{ 
            fontSize: '12px', 
            color: '#94a3b8', 
            fontWeight: 'bold',
            letterSpacing: '1px',
            margin: '25px 0 10px 10px',
            display: sidebarCollapsed ? 'none' : 'block'
          }}>
            WORKSPACE
          </p>
          
          <MenuItem 
            icon="💼" 
            title="Projekte" 
            active={false} 
            collapsed={sidebarCollapsed} 
          />
          <MenuItem 
            icon="📱" 
            title="App-Versionen" 
            active={false} 
            collapsed={sidebarCollapsed} 
          />
          <MenuItem 
            icon="⚙️" 
            title="Einstellungen" 
            active={false} 
            collapsed={sidebarCollapsed} 
          />
        </div>
        
        {/* Toggle Button */}
        <button 
          onClick={toggleSidebar}
          style={{
            position: 'absolute',
            bottom: '20px',
            left: sidebarCollapsed ? '20px' : '200px',
            border: 'none',
            background: '#f0eafb',
            color: '#5b21b6',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            transition: 'left 0.3s ease',
            fontSize: '16px'
          }}
        >
          {sidebarCollapsed ? '→' : '←'}
        </button>
      </div>

      {/* Main Content Area */}
      <div style={{ 
        marginLeft: sidebarCollapsed ? '80px' : '250px', 
        flexGrow: 1,
        transition: 'margin-left 0.3s ease',
        width: 'calc(100% - ' + (sidebarCollapsed ? '80px' : '250px') + ')'
      }}>
        {/* Header */}
        <header style={{ 
          background: 'linear-gradient(135deg, #4a00e0, #8e2de2)',
          color: 'white',
          padding: '15px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px' }}>
              {view === 'list' ? 'Dashboard' : 
               view === 'tickets' ? 'Tickets' :
               view === 'todo' ? 'Aufgaben' :
               view === 'calendar' ? 'Kalender' : 
               view === 'analytics' ? 'Statistiken' : 'Dashboard'}
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              marginRight: '20px',
              textAlign: 'right',
              lineHeight: '1.2'
            }}>
              <div style={{ fontSize: '14px' }}>{formatDate(currentTime)}</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{formatTime(currentTime)}</div>
            </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              background: 'rgba(255,255,255,0.2)',
              padding: '8px 15px',
              borderRadius: '30px'
            }}>
              <span style={{ marginRight: '10px' }}>👤 {auth.currentUser?.email}</span>
              <button 
                onClick={handleLogout}
                style={{ 
                  background: 'rgba(255,255,255,0.9)', 
                  color: '#5b21b6',
                  padding: '5px 12px',
                  border: 'none',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '13px'
                }}
              >
                Abmelden
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
          {/* Nur auf dem Dashboard die Übersicht anzeigen */}
          {view === 'list' && (
            <>
              {/* Welcome Section */}
              <div style={{ 
                marginBottom: '25px', 
                padding: '25px',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
              }}>
                <h2 style={{ margin: '0 0 5px 0', color: '#333' }}>
                  Hallo Admin, willkommen zurück!
                </h2>
                <p style={{ margin: '0', color: '#666' }}>
                  Hier ist eine Übersicht der aktuellen Feedback-Tickets
                </p>
              </div>
              
              {/* Stats Cards */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', 
                gap: '20px',
                marginBottom: '25px'
              }}>
                <StatCard title="Gesamt" value={stats.total} color="#4a00e0" icon="📊" />
                <StatCard title="Neue Tickets" value={stats.new} color="#00bcd4" icon="🆕" />
                <StatCard title="In Bearbeitung" value={stats.inProgress} color="#ff9800" icon="🔄" />
                <StatCard title="Erledigt" value={stats.resolved} color="#4caf50" icon="✅" />
                <StatCard title="Hohe Priorität" value={stats.highPriority} color="#f44336" icon="🔥" />
              </div>
              
              {/* Navigation Tabs */}
              <div style={{ 
                display: 'flex', 
                backgroundColor: 'white', 
                padding: '5px', 
                borderRadius: '12px',
                marginBottom: '25px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                <TabButton 
                  title="Listen-Ansicht" 
                  icon="📋" 
                  active={true} 
                  onClick={() => {}} 
                />
                <TabButton 
                  title="Kanban-Board" 
                  icon="📌" 
                  active={false} 
                  onClick={() => setView('kanban')} 
                />
                <TabButton 
                  title="Aufgaben" 
                  icon="✅" 
                  active={false} 
                  onClick={() => setView('todo')} 
                />
                <TabButton 
                  title="Kalender" 
                  icon="📅" 
                  active={false} 
                  onClick={() => setView('calendar')} 
                />
              </div>
            </>
          )}
          
          {/* Content Area */}
          <div style={{ 
            backgroundColor: 'white', 
            padding: '25px', 
            borderRadius: '12px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}>
            {loading && view !== 'todo' ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px', 
                color: '#666' 
              }}>
                <div style={{ 
                  display: 'inline-block',
                  width: '50px',
                  height: '50px',
                  border: '5px solid #f3f3f3',
                  borderTop: '5px solid #5b21b6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginBottom: '15px'
                }}></div>
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
                <p>Daten werden geladen...</p>
              </div>
            ) : view === 'list' || view === 'tickets' ? (
              <TicketList tickets={tickets} />
            ) : view === 'todo' ? (
              <TodoView />
            ) : view === 'kanban' ? (
              <div style={{ padding: '20px 0' }}>
                <h3 style={{ marginTop: 0 }}>Kanban-Board</h3>
                <p>Diese Funktion wird bald verfügbar sein.</p>
              </div>
            ) : view === 'calendar' ? (
              <div style={{ padding: '20px 0' }}>
                <h3 style={{ marginTop: 0 }}>Kalender-Ansicht</h3>
                <p>Diese Funktion wird bald verfügbar sein.</p>
              </div>
            ) : (
              <div style={{ padding: '20px 0' }}>
                <h3 style={{ marginTop: 0 }}>Statistiken</h3>
                <p>Diese Funktion wird bald verfügbar sein.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper Component: Stat Card
function StatCard({ title, value, color, icon }) {
  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      display: 'flex',
      alignItems: 'center',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    }}>
      <div style={{
        width: '50px',
        height: '50px',
        borderRadius: '12px',
        backgroundColor: `${color}15`,
        color: color,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '24px',
        marginRight: '15px'
      }}>
        {icon}
      </div>
      <div>
        <h3 style={{ margin: '0', fontSize: '14px', color: '#666' }}>{title}</h3>
        <p style={{ margin: '5px 0 0 0', fontSize: '24px', fontWeight: 'bold', color: '#333' }}>{value}</p>
      </div>
    </div>
  );
}

// Helper Component: Tab Button
function TabButton({ title, icon, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px 20px',
        background: active ? '#f0eafb' : 'transparent',
        color: active ? '#5b21b6' : '#666',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: active ? 'bold' : 'normal',
        transition: 'all 0.2s ease'
      }}
    >
      <span style={{ marginRight: '8px', fontSize: '18px' }}>{icon}</span>
      {title}
    </button>
  );
}

// Helper Component: Menu Item
function MenuItem({ icon, title, active, collapsed, onClick = () => {} }) {
  return (
    <div 
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: collapsed ? '12px 0' : '12px 15px',
        marginBottom: '5px',
        borderRadius: '8px',
        backgroundColor: active ? '#f0eafb' : 'transparent',
        color: active ? '#5b21b6' : '#64748b',
        cursor: 'pointer',
        fontWeight: active ? 'bold' : 'normal',
        justifyContent: collapsed ? 'center' : 'flex-start',
        transition: 'all 0.2s ease'
      }}
    >
      <span style={{ 
        fontSize: '18px', 
        marginRight: collapsed ? '0' : '12px'
      }}>
        {icon}
      </span>
      {!collapsed && <span>{title}</span>}
    </div>
  );
}

export default Dashboard;